// Sprint chart helpers for the Insights page's widgets (pages/insights.js). Pure (no DOM, no api.js)
// so node:test can import it without stubs — same shape as lib/sprint-dates.js.
//
// Burndown (TPT590): which sprint opens by default, why a sprint can't be charted, the chart's
// geometry and its inline SVG. The input is the GET /projects/:id/sprints/:sprintId/burndown body
// (tt-api-sprints.md § Burndown): `{ sprint, unit, total, days: [{ date, ideal, remaining }] }`, one
// point per calendar day, `remaining` null after the database's current day.
//
// Velocity (TPT9103): grouped committed/completed columns and the rolling average, at the end of
// this file. The input is the `sprints` list of GET /projects/:id/sprints/velocity
// (tt-api-sprints.md § Velocity), newest first.

import { todayIso, dueDayDiff, shortDay } from './due-tasks.js';

// Inclusive calendar-day limit, matching MAX_SPRINT_DAYS in the API sprint routes.
// Pre-check saved dates to avoid requesting a legacy sprint the endpoint would reject.
export const MAX_CHART_DAYS = 730;

const MIN_WIDTH = 240;
const MIN_HEIGHT = 160;
// Date labels are centred on their day, so both sides keep room for half a label ("Oct 26").
const PAD = { top: 22, right: 22, bottom: 26, left: 22 };
const DIGIT_PX = 7; // advance of one 11px tick-label digit, sizes the left margin
const DAY_STEPS = [1, 2, 7, 14, 28, 56, 91, 182, 364];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Markup text, for element content and attribute values alike. utils.js escapeHtml() serializes a
// text node, which leaves `"` unescaped (unsafe inside an attribute) and turns 0 into ''.
const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export function escapeText(value) {
  return String(value ?? '').replace(/[&<>"']/g, ch => ESCAPES[ch]);
}

function finite(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function round(value) {
  return Math.round(value * 10) / 10;
}

// A sprint date as a usable `YYYY-MM-DD` string, or null (unset or malformed).
function day(value) {
  return typeof value === 'string' && shortDay(value) ? value : null;
}

// The burndown endpoint's reply for a sprint without both planned dates.
export function isSprintDatesMissing(err) {
  return !!err && err.status === 400 && !!err.body && err.body.error === 'sprint_dates_missing';
}

// Why a sprint row can't be charted, or null when it can: 'datesMissing' without two usable dates
// (the endpoint would answer 400 sprint_dates_missing), 'tooLong' past MAX_CHART_DAYS.
export function sprintChartBlock(sprint) {
  const start = sprint && day(sprint.start_date);
  const end = sprint && day(sprint.end_date);
  const span = start && end ? dueDayDiff(end, start) : null;
  if (span == null || span < 0) return 'datesMissing';
  return span + 1 > MAX_CHART_DAYS ? 'tooLong' : null;
}

// The sprint the burndown opens on. Only real sprints count: number 0 is the backlog
// (board-filters.js skips it the same way). In order:
//   1. a sprint that isn't closed and whose dates contain today — latest start, then highest number;
//   2. the dated sprint nearest today: days since an ended one's end, or until an upcoming one's
//      start (a closed sprint whose dates contain today is 0 away). A tie goes to the one that has
//      started, which has data to show;
//   3. the highest-numbered sprint that isn't closed (Sprint Planning's own pick);
//   4. the highest-numbered sprint.
export function pickCurrentSprint(sprints, today = todayIso()) {
  const list = (sprints || []).filter(s => s && Number(s.number) > 0);
  if (!list.length) return null;
  const byNumber = (a, b) => b.number - a.number;

  const running = [];
  const dated = [];
  for (const s of list) {
    const start = day(s.start_date);
    const end = day(s.end_date);
    if (!start || !end) continue;
    const untilStart = dueDayDiff(start, today);
    const sinceEnd = dueDayDiff(today, end);
    if (untilStart <= 0 && sinceEnd <= 0 && s.status !== 'closed') running.push(s);
    dated.push({ sprint: s, started: untilStart <= 0, distance: Math.max(untilStart, sinceEnd, 0) });
  }
  if (running.length) {
    return running.sort((a, b) => b.start_date.localeCompare(a.start_date) || byNumber(a, b))[0];
  }
  if (dated.length) {
    dated.sort((a, b) => a.distance - b.distance || Number(b.started) - Number(a.started) || byNumber(a.sprint, b.sprint));
    return dated[0].sprint;
  }
  const open = list.filter(s => s.status !== 'closed');
  return (open.length ? open : list).slice().sort(byNumber)[0];
}

// Y-axis values: whole numbers on a 1/2/5 × 10^k step with at most five intervals, the top tick at or
// above `max` and never below 1, so an all-zero series still has a scale.
export function yTicks(max) {
  const n = Number(max);
  const top = Math.max(1, Math.ceil(Number.isFinite(n) ? n : 0));
  let step = 1;
  for (let magnitude = 1; ; magnitude *= 10) {
    const found = [1, 2, 5].map(m => m * magnitude).find(s => top / s <= 5);
    if (found) { step = found; break; }
  }
  const ticks = [];
  for (let v = 0; v <= Math.ceil(top / step) * step; v += step) ticks.push(v);
  return ticks;
}

// Which days carry an x-axis date label: every `step` days from the first, `step` being the smallest
// of DAY_STEPS that keeps labels `minGap` px apart. The first and last day are always labelled; the
// last regular label gives way when it would crowd the final one.
export function xTickIndexes(count, plotWidth, minGap = 56) {
  if (!(count > 0)) return [];
  if (count === 1) return [0];
  const dayPx = Math.max(0, finite(plotWidth)) / (count - 1);
  const step = DAY_STEPS.find(s => s * dayPx >= minGap) || count;
  const out = [];
  for (let i = 0; i < count; i += step) out.push(i);
  const last = count - 1;
  if (out[out.length - 1] !== last) {
    if (out.length > 1 && (last - out[out.length - 1]) * dayPx < minGap) out.pop();
    out.push(last);
  }
  return out;
}

// Chart layout in SVG user units for a `width` × `height` viewBox. Every coordinate is finite and
// rounded to 0.1. `remaining` is plotted over its numeric prefix (the observed days); a one-day
// sprint is a single centred column with no lines.
//
// Today marker (`todayIndex`): the endpoint stops `remaining` at the database's current day, so the
// marker sits where that line ends — on the sprint's last day only when that day is the viewer's
// today (afterwards the sprint is over), and nowhere before the sprint starts. Deriving it from the
// data keeps it on the line's end even when the viewer's calendar day differs from the server's.
export function buildBurndownGeometry(data, { width = 640, height = 240, today = todayIso() } = {}) {
  const w = Math.max(MIN_WIDTH, Math.round(finite(width)));
  const h = Math.max(MIN_HEIGHT, Math.round(finite(height)));
  const days = Array.isArray(data && data.days) ? data.days : [];
  const n = days.length;

  let max = finite(data && data.total);
  for (const d of days) {
    max = Math.max(max, finite(d.ideal), d.remaining == null ? 0 : finite(d.remaining));
  }
  const ticks = yTicks(max);
  const yMax = ticks[ticks.length - 1];
  const left = Math.max(PAD.left, 12 + DIGIT_PX * Math.max(...ticks.map(t => String(t).length)));
  const plot = { left, top: PAD.top, right: w - PAD.right, bottom: h - PAD.bottom };
  const plotW = plot.right - plot.left;
  const plotH = plot.bottom - plot.top;
  const xAt = i => (n <= 1 ? plot.left + plotW / 2 : plot.left + (plotW * i) / (n - 1));
  const yAt = v => plot.bottom - (plotH * finite(v)) / yMax;

  const points = days.map((d, i) => ({
    date: d.date,
    ideal: finite(d.ideal),
    remaining: d.remaining == null ? null : finite(d.remaining),
    x: round(xAt(i)),
    yIdeal: round(yAt(d.ideal)),
    yRemaining: d.remaining == null ? null : round(yAt(d.remaining)),
  }));
  let observed = 0;
  while (observed < n && points[observed].remaining != null) observed += 1;
  const lastIndex = observed - 1;

  let todayIndex = null;
  if (lastIndex >= 0 && lastIndex < n - 1) todayIndex = lastIndex;
  else if (n > 0 && lastIndex === n - 1 && days[n - 1].date === today) todayIndex = n - 1;

  const line = pts => pts.map((p, i) => `${i ? 'L' : 'M'}${p[0]} ${p[1]}`).join(' ');
  return {
    width: w,
    height: h,
    plot,
    points,
    lastIndex,
    todayIndex,
    idealPath: n > 1 ? line(points.map(p => [p.x, p.yIdeal])) : '',
    remainingPath: observed > 1 ? line(points.slice(0, observed).map(p => [p.x, p.yRemaining])) : '',
    yTicks: ticks.map(value => ({ value, y: round(yAt(value)) })),
    xTicks: xTickIndexes(n, plotW).map(i => ({ index: i, x: points[i].x, label: shortDay(points[i].date) })),
  };
}

// The day under an x position (SVG user units), clamped to the sprint; -1 without days.
export function nearestIndex(geom, x) {
  const n = geom && geom.points ? geom.points.length : 0;
  if (n <= 1) return n - 1;
  const { left, right } = geom.plot;
  const t = (finite(x) - left) / (right - left);
  return Math.min(n - 1, Math.max(0, Math.round(t * (n - 1))));
}

// Where the sprint stands on its last observed day:
//   'upcoming' — no observed day yet (the sprint hasn't started);
//   'running'  — observed up to a day before the end, or the end is today; `delta` is remaining
//                minus ideal on that day (positive = behind the ideal line);
//   'ended'    — observed through the last day, which has passed.
export function burndownSummary(data, geom) {
  const total = finite(data && data.total);
  const unit = data && data.unit === 'story_points' ? 'story_points' : 'tasks';
  if (!geom || geom.lastIndex < 0) {
    return { phase: 'upcoming', unit, total, remaining: null, ideal: null, delta: null, date: null };
  }
  const p = geom.points[geom.lastIndex];
  const ended = geom.lastIndex === geom.points.length - 1 && geom.todayIndex == null;
  return {
    phase: ended ? 'ended' : 'running',
    unit,
    total,
    remaining: p.remaining,
    ideal: p.ideal,
    delta: p.remaining - p.ideal,
    date: p.date,
  };
}

// A chart value with at most one decimal: 8, 4.3. The ideal line is fractional; work counts aren't.
export function formatValue(value) {
  const n = Math.round(finite(value) * 10) / 10;
  return String(Object.is(n, -0) ? 0 : n);
}

// "1 story point", "8 story points", "3 tasks".
export function formatAmount(value, unit) {
  const text = formatValue(value);
  const noun = unit === 'story_points' ? 'story point' : 'task';
  return `${text} ${noun}${text === '1' ? '' : 's'}`;
}

// "Tue, Oct 20" for a `YYYY-MM-DD` string — burndowns run over weekends, so the weekday matters.
export function dayLabel(iso) {
  const label = shortDay(iso);
  if (!label) return '';
  const weekday = WEEKDAYS[new Date(`${iso}T00:00:00Z`).getUTCDay()];
  return `${weekday}, ${label}`;
}

// The value readout for one day: "Tue, Oct 20 · 5 remaining · 4.3 ideal".
export function burndownReadout(geom, index) {
  const p = geom && geom.points[index];
  if (!p) return '';
  const remaining = p.remaining == null ? 'no data yet' : `${formatValue(p.remaining)} remaining`;
  return `${dayLabel(p.date)} · ${remaining} · ${formatValue(p.ideal)} ideal`;
}

// The day the readout shows while nothing is hovered: today, else the last observed day, else the
// first day (before the sprint starts).
export function defaultReadoutIndex(geom) {
  if (!geom || !geom.points.length) return -1;
  if (geom.todayIndex != null) return geom.todayIndex;
  return Math.max(0, geom.lastIndex);
}

function anchorWithin(x, plot) {
  if (x < plot.left + 24) return 'start';
  if (x > plot.right - 24) return 'end';
  return 'middle';
}

// The chart as an SVG string. Colours come from CSS classes over palette tokens (styles.css
// § Insights), never from resolved hexes, so a palette switch repaints it; `label` is the accessible
// summary. The `.bd-hover` group (crosshair + one dot per line) starts hidden; pages/insights.js
// moves it.
export function renderBurndownSvg(geom, { label = '' } = {}) {
  const { width, height, plot, points } = geom;
  const out = [`<svg class="bd-svg" viewBox="0 0 ${width} ${height}" role="img" aria-label="${escapeText(label)}">`];
  for (const t of geom.yTicks) {
    if (t.value > 0) out.push(`<line class="bd-grid" x1="${plot.left}" y1="${t.y}" x2="${plot.right}" y2="${t.y}"/>`);
    out.push(`<text class="bd-tick" x="${plot.left - 8}" y="${t.y}" text-anchor="end" dominant-baseline="middle">${escapeText(t.value)}</text>`);
  }
  out.push(`<line class="bd-baseline" x1="${plot.left}" y1="${plot.bottom}" x2="${plot.right}" y2="${plot.bottom}"/>`);
  for (const t of geom.xTicks) {
    out.push(`<text class="bd-tick" x="${t.x}" y="${plot.bottom + 17}" text-anchor="middle">${escapeText(t.label)}</text>`);
  }
  if (geom.todayIndex != null) {
    const x = points[geom.todayIndex].x;
    out.push(`<line class="bd-today" x1="${x}" y1="${plot.top}" x2="${x}" y2="${plot.bottom}"/>`);
    out.push(`<text class="bd-today-label" x="${x}" y="${plot.top - 8}" text-anchor="${anchorWithin(x, plot)}">Today</text>`);
  }
  if (geom.idealPath) out.push(`<path class="bd-ideal" d="${geom.idealPath}" stroke-dasharray="6 5"/>`);
  if (geom.remainingPath) out.push(`<path class="bd-remaining" d="${geom.remainingPath}"/>`);
  if (geom.lastIndex >= 0) {
    const p = points[geom.lastIndex];
    // Above-right of the dot; below it near the top edge (where "Today" sits), left of it near the right.
    const nearRight = p.x > plot.right - 28;
    const lx = nearRight ? p.x - 8 : p.x + 8;
    const ly = p.yRemaining - plot.top < 18 ? p.yRemaining + 16 : p.yRemaining - 8;
    out.push(`<circle class="bd-end-dot" cx="${p.x}" cy="${p.yRemaining}" r="4"/>`);
    out.push(`<text class="bd-end-label" x="${round(lx)}" y="${round(ly)}" text-anchor="${nearRight ? 'end' : 'start'}">${escapeText(formatValue(p.remaining))}</text>`);
  }
  out.push(`<g class="bd-hover"><line class="bd-crosshair" x1="${plot.left}" y1="${plot.top}" x2="${plot.left}" y2="${plot.bottom}"/>`
    + `<circle class="bd-hover-dot bd-hover-ideal" cx="${plot.left}" cy="${plot.bottom}" r="4"/>`
    + `<circle class="bd-hover-dot bd-hover-remaining" cx="${plot.left}" cy="${plot.bottom}" r="4"/></g>`);
  out.push('</svg>');
  return out.join('');
}

// ── Velocity ──

// Sprints the rolling average spans. Sprint Planning's default capacity is this same average.
export const VELOCITY_WINDOW = 3;

const COLUMN_MAX = 24;    // a column never fills its band, however few sprints there are
const COLUMN_GAP = 2;     // card ground between a group's two columns
const COLUMN_CORNER = 4;  // rounded data end; the baseline end stays square
const COLUMN_MIN = 2;     // a value above zero stays visible on a scale it barely registers on
const GROUP_SHARE = 0.72; // of a band; the rest is the air that separates sprints
const NUMBER_GAP = 34;    // px between sprint labels, room for "#406"

// Velocity is completed points: the mean over the newest `window` rows, or over what exists while
// there are fewer. `rows` runs newest first. { average: null, count: 0 } without rows.
export function rollingVelocity(rows, window = VELOCITY_WINDOW) {
  const recent = (rows || []).slice(0, window);
  if (!recent.length) return { average: null, count: 0 };
  return { average: recent.reduce((sum, row) => sum + finite(row.completed), 0) / recent.length, count: recent.length };
}

function columnPath(x, top, width, bottom) {
  const r = Math.min(COLUMN_CORNER, width / 2, bottom - top);
  const [x0, x1, x2, x3] = [x, x + r, x + width - r, x + width].map(round);
  return `M${x0} ${round(bottom)}V${round(top + r)}Q${x0} ${round(top)} ${x1} ${round(top)}`
    + `H${x2}Q${x3} ${round(top)} ${x3} ${round(top + r)}V${round(bottom)}Z`;
}

// The chart in SVG user units for a `width` × `height` viewBox: sprints left to right from oldest to
// newest, whatever the rows' order, one group per sprint with the two columns growing from one
// baseline. `rolling` on a group is the average as it stood at that sprint, so its first two groups
// average what exists. `average`/`averageCount` are the newest group's, the figure the page reports.
// The right gutter keeps room for the line's end value, so no column can sit under it. Every
// coordinate is finite and rounded to 0.1.
export function velocityBars(rows, { width = 360, height = 240 } = {}) {
  const w = Math.max(MIN_WIDTH, Math.round(finite(width)));
  const h = Math.max(MIN_HEIGHT, Math.round(finite(height)));
  const newestFirst = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const n = newestFirst.length;
  const { average, count } = rollingVelocity(newestFirst);

  const peak = newestFirst.reduce((most, row) => Math.max(most, finite(row.committed), finite(row.completed)), 0);
  const ticks = yTicks(peak);
  const yMax = ticks[ticks.length - 1];
  const left = Math.max(PAD.left, 12 + DIGIT_PX * Math.max(...ticks.map(t => String(t).length)));
  const gutter = average == null ? PAD.right : Math.max(PAD.right, 12 + DIGIT_PX * formatValue(average).length);
  const plot = { left, top: PAD.top, right: w - gutter, bottom: h - PAD.bottom };
  const plotH = plot.bottom - plot.top;
  const yAt = v => plot.bottom - (plotH * finite(v)) / yMax;
  const band = (plot.right - plot.left) / Math.max(1, n);
  const columnW = Math.max(1, Math.min(COLUMN_MAX, (band * GROUP_SHARE - COLUMN_GAP) / 2));

  const column = (x, value) => {
    const top = finite(value) > 0 ? Math.min(yAt(value), plot.bottom - COLUMN_MIN) : plot.bottom;
    return {
      x: round(x),
      y: round(top),
      width: round(columnW),
      height: round(plot.bottom - top),
      path: top < plot.bottom ? columnPath(x, top, columnW, plot.bottom) : '',
    };
  };

  const groups = newestFirst
    .map((row, i) => ({ row, rolling: rollingVelocity(newestFirst.slice(i)).average }))
    .reverse()
    .map(({ row, rolling }, i) => {
      const x = plot.left + band * (i + 0.5);
      return {
        id: row.id,
        number: row.number,
        name: row.name,
        committed: finite(row.committed),
        completed: finite(row.completed),
        rolling,
        x: round(x),
        yRolling: round(yAt(rolling)),
        band: { x: round(plot.left + band * i), width: round(band) },
        committedBar: column(x - columnW - COLUMN_GAP / 2, row.committed),
        completedBar: column(x + COLUMN_GAP / 2, row.completed),
      };
    });

  return {
    width: w,
    height: h,
    plot,
    groups,
    average,
    averageCount: count,
    rollingPath: n > 1 ? groups.map((g, i) => `${i ? 'L' : 'M'}${g.x} ${g.yRolling}`).join(' ') : '',
    yTicks: ticks.map(value => ({ value, y: round(yAt(value)) })),
    xTicks: xTickIndexes(n, band * (n - 1), NUMBER_GAP).map(i => ({ index: i, x: groups[i].x, label: `#${groups[i].number}` })),
  };
}

// The group under an x position (SVG user units), clamped to the chart; -1 without groups.
export function nearestGroup(geom, x) {
  const n = geom && geom.groups ? geom.groups.length : 0;
  if (n === 0) return -1;
  const { left, right } = geom.plot;
  const at = Math.floor(((finite(x) - left) / (right - left)) * n);
  return Math.min(n - 1, Math.max(0, at));
}

// The value readout for one group: "Sprint 12 · 21 committed · 18 completed · 19.3 average".
// `nameOf` names a group, since only the page knows the project's noun.
export function velocityReadout(geom, index, nameOf = g => `#${g.number}`) {
  const g = geom && geom.groups[index];
  if (!g) return '';
  return `${nameOf(g)} · ${formatValue(g.committed)} committed · ${formatValue(g.completed)} completed · ${formatValue(g.rolling)} average`;
}

// The chart as an SVG string; colours come from CSS classes over palette tokens, as in
// renderBurndownSvg(). The `.vc-hover` band starts hidden and pages/insights.js moves it. The line
// runs under a wider card-ground stroke so it stays readable across the columns it crosses.
export function renderVelocitySvg(geom, { label = '' } = {}) {
  const { width, height, plot, groups } = geom;
  const out = [`<svg class="bd-svg" viewBox="0 0 ${width} ${height}" role="img" aria-label="${escapeText(label)}">`];
  out.push(`<rect class="vc-hover" x="${plot.left}" y="${plot.top}" width="0" height="${round(plot.bottom - plot.top)}"/>`);
  for (const t of geom.yTicks) {
    if (t.value > 0) out.push(`<line class="bd-grid" x1="${plot.left}" y1="${t.y}" x2="${plot.right}" y2="${t.y}"/>`);
    out.push(`<text class="bd-tick" x="${plot.left - 8}" y="${t.y}" text-anchor="end" dominant-baseline="middle">${escapeText(t.value)}</text>`);
  }
  for (const g of groups) {
    if (g.committedBar.path) out.push(`<path class="vc-committed" d="${g.committedBar.path}"/>`);
    if (g.completedBar.path) out.push(`<path class="vc-completed" d="${g.completedBar.path}"/>`);
  }
  out.push(`<line class="bd-baseline" x1="${plot.left}" y1="${plot.bottom}" x2="${plot.right}" y2="${plot.bottom}"/>`);
  for (const t of geom.xTicks) {
    out.push(`<text class="bd-tick" x="${t.x}" y="${plot.bottom + 17}" text-anchor="middle">${escapeText(t.label)}</text>`);
  }
  if (geom.rollingPath) {
    out.push(`<path class="vc-rolling-halo" d="${geom.rollingPath}"/>`);
    out.push(`<path class="vc-rolling" d="${geom.rollingPath}"/>`);
  }
  const newest = groups[groups.length - 1];
  if (newest) {
    // Just past the newest columns: nothing sits right of them, and with few sprints the gutter is far.
    const labelX = round(Math.min(newest.completedBar.x + newest.completedBar.width, plot.right) + 6);
    out.push(`<circle class="vc-end-dot" cx="${newest.x}" cy="${newest.yRolling}" r="4"/>`);
    out.push(`<text class="vc-end-label" x="${labelX}" y="${newest.yRolling}" dominant-baseline="middle">${escapeText(formatValue(newest.rolling))}</text>`);
  }
  out.push('</svg>');
  return out.join('');
}
